"use client";

import { TextShimmer } from "@/components/text-shimmer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { encodedUmrahParams } from "@/feature/umrah/package/search/lib/umrah-formatter-helper";
import { priceFormatter } from "@/lib/price-formatter";
import { absoluteUrl } from "@/lib/utils";
import { ArrowRight, CalendarClock, Users } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useTransition } from "react";
import {
  IGetUmrahList,
  IUmrahSearchQueryParams,
} from "../../types/umrah-search-type";

interface UmrahListCardProps {
  umrah: IGetUmrahList;
  searchData: IUmrahSearchQueryParams;
}

function parsePrice(value: string | number | null | undefined): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const normalized = Number(value.replaceAll(",", ""));
    if (Number.isFinite(normalized)) {
      return normalized;
    }
  }

  return null;
}

export default function UmrahListCard({
  umrah,
  searchData,
}: UmrahListCardProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const params = encodedUmrahParams(searchData);
  const detailsHref = params
    ? `/umrah/${encodeURIComponent(umrah.slug)}?${params}`
    : `/umrah/${encodeURIComponent(umrah.slug)}`;

  const parsedPrice = parsePrice(umrah.adult_price);
  const hasPrice = typeof parsedPrice === "number" && parsedPrice > 0;
  const summaryText =
    umrah.short_description ||
    "Premium umrah package with curated itinerary, accommodation, and service support.";

  const handleViewDetails = () => {
    startTransition(() => {
      router.push(detailsHref);
    });
  };

  return (
    <Card className="group w-full overflow-hidden rounded-2xl border border-border/60 bg-background p-0 shadow-sm transition-all duration-200 hover:shadow-md">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_13rem]">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
          <div className="relative h-56 w-full overflow-hidden md:h-full">
            <Image
              src={absoluteUrl(
                umrah.thumbnail || "https://placehold.co/600x400/png",
                true,
              )}
              alt={umrah.title}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              sizes="(max-width: 768px) 100vw, 300px"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

            <div className="absolute left-3 top-3 flex flex-wrap items-center gap-2">
              <Badge className="border border-white/30 bg-black/35 text-white hover:bg-black/35">
                Umrah Package
              </Badge>
              <Badge
                variant="outline"
                className="border-white/35 bg-black/20 text-white hover:bg-black/20"
              >
                {umrah.duration} Days
              </Badge>
            </div>
          </div>

          <div className="flex min-w-0 flex-col justify-between p-4 md:p-5">
            <div className="space-y-3">
              <h3 className="line-clamp-2 text-lg font-semibold leading-snug md:text-xl">
                {umrah.title}
              </h3>
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {summaryText}
              </p>

              <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                <p className="inline-flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 shrink-0 text-primary" />
                  <span>{umrah.duration} Days</span>
                </p>
              </div>
            </div>
            <div className="mt-4">
              <p className="inline-flex items-center gap-2">
                <Users className="h-4 w-4 shrink-0 text-primary" />
                <span>Up to {umrah.group_size} pilgrims</span>
              </p>
            </div>
          </div>
        </div>

        <aside className="flex w-full flex-col justify-between border-l border-border/60 bg-gradient-to-b from-primary/[0.08] to-background p-4 text-right md:p-5">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Starting from
            </p>
            {hasPrice ? (
              <p className="mt-1 text-2xl font-bold text-primary">
                {priceFormatter(parsedPrice)}
              </p>
            ) : (
              <p className="mt-1 text-lg font-semibold text-primary">
                Price on request
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">Per adult</p>
          </div>

          <div className="mt-5">
            <Button
              onClick={handleViewDetails}
              className="w-full rounded-lg"
              disabled={isPending}
            >
              {isPending ? (
                <TextShimmer>View Details</TextShimmer>
              ) : (
                "View Details"
              )}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </aside>
      </div>
    </Card>
  );
}
